import { el } from 'redom';

import application from 'application';
import serverInterface from 'server-interface';

import recurringFrequencies from 'constants/recurring-frequencies';
import attendeeStatuses from 'constants/attendee-statuses';

import SingleSelectInput from 'generics/components/single-select/native';

import {
  findIndexByPropertyValue
} from 'utilities/collection';

import MeetingAttendees from './attendees';

import {
  findAttendee,
  getAllUpcomingMeetingStatuesConfirmPromptHeader
} from './utilities';

import './index.scss';

class MeetingStatusControl {

  constructor(configuration) {

    this.activity = configuration.activity;
    this.userId = configuration.userId;
    this.attendee = findAttendee(this.activity.event.attendees, this.userId);
    this.onChange = configuration.onChange;
    this.accept = configuration.accept;
    this.acceptAll = configuration.acceptAll;
    this.decline = configuration.decline;
    this.declineAll = configuration.declineAll;
    this.mayAttend = configuration.mayAttend;
    this.mayAttendAll = configuration.mayAttendAll;

    this.el = el('#meeting-status-control-main', [
      this.renderStatus()
    ]);

  }

  getStatusInputOptions() {

    const options = [{
      label: 'Going',
      value: attendeeStatuses.ACCEPTED
    }, {
      label: 'May attend',
      value: attendeeStatuses.MAY_BE
    }, {
      label: 'Declined',
      value: attendeeStatuses.DECLINED
    }];

    return options;

  }

  getSelectedStatusIndices() {

    const index = findIndexByPropertyValue(this.getStatusInputOptions(), 'value', this.attendee.status);

    if (index === -1) {
      return [];
    }

    return [index];

  }

  buildStatusInputOptions() {

    const options = {
      placeholder: 'Pending',
      options: this.getStatusInputOptions(),
      selectedOptionIndices: this.getSelectedStatusIndices(),
      onChange: this.handleStatusChange.bind(this)
    };

    return options;

  }

  renderStatusInput() {

    return new SingleSelectInput(this.buildStatusInputOptions());

  }

  renderStatus() {

    if (this.activity.event.creatorId === this.userId) {
      return 'Going';
    } else {
      return this.renderStatusInput();
    }

  }

  renderAttendees() {

    return new MeetingAttendees(this.activity);

  }

  async acceptMeeting(meetingId) {

    if (typeof this.accept === 'function') {
      await this.accept(meetingId);
    } else {
      await serverInterface.startup.calendarActivities.meetings.attendMeeting(meetingId);
    }

  }

  async acceptAllUpcomingMeetings(meetingId) {

    if (typeof this.acceptAll === 'function') {
      await this.acceptAll(meetingId);
    } else {
      await serverInterface.startup.calendarActivities.meetings.attendAllUpcomingMeetings(meetingId);
    }

  }

  async declineMeeting(meetingId) {

    if (typeof this.decline === 'function') {
      await this.decline(meetingId);
    } else {
      await serverInterface.startup.calendarActivities.meetings.declineMeeting(meetingId);
    }

  }

  async declineAllUpcomingMeetings(meetingId) {

    if (typeof this.declineAll === 'function') {
      await this.declineAll(meetingId);
    } else {
      await serverInterface.startup.calendarActivities.meetings.declineAllUpcomingMeetings(meetingId);
    }

  }

  async mayAttendMeeting(meetingId) {

    if (typeof this.mayAttend === 'function') {
      await this.mayAttend(meetingId);
    } else {
      await serverInterface.startup.calendarActivities.meetings.mayAttendMeeting(meetingId);
    }

  }

  async mayAttendAllUpcomingMeetings(meetingId) {

    if (typeof this.mayAttendAll === 'function') {
      await this.mayAttendAll(meetingId);
    } else {
      await serverInterface.startup.calendarActivities.meetings.mayAttendAllUpcomingMeetings(meetingId);
    }

  }

  async setOnetimeMeetingStatus(status) {

    const meetingId = this.activity.event._id;

    if (status === attendeeStatuses.ACCEPTED) {
      await this.acceptMeeting(meetingId);
    } else if (status === attendeeStatuses.DECLINED) {
      await this.declineMeeting(meetingId);
    } else if (status === attendeeStatuses.MAY_BE) {
      await this.mayAttendMeeting(meetingId);
    }

    this.onChange();

  }

  async setAllUpcomingMeetingStatus(status) {

    const meetingId = this.activity.event._id;

    if (status === attendeeStatuses.ACCEPTED) {
      await this.acceptAllUpcomingMeetings(meetingId);
    } else if (status === attendeeStatuses.DECLINED) {
      await this.declineAllUpcomingMeetings(meetingId);
    } else if (status === attendeeStatuses.MAY_BE) {
      await this.mayAttendAllUpcomingMeetings(meetingId);
    }

    this.onChange();

  }

  async trySetRecurringMeetingStatus(status) {

    const confirmed = await application.prompts.confirm({
      header: getAllUpcomingMeetingStatuesConfirmPromptHeader(status),
      description: '',
      confirmButtonText: 'Yes',
      cancelButtonText: 'No, only this one!'
    });

    if (confirmed === false) {
      await this.setOnetimeMeetingStatus(status);
    } else {
      await this.setAllUpcomingMeetingStatus(status);
    }

  }

  async setAttendeeStatus(status) {

    this.attendee.status = status;

    if (this.activity.event.recurring.freq === recurringFrequencies.NEVER) {

      await this.setOnetimeMeetingStatus(status);

    } else {

      await this.trySetRecurringMeetingStatus(status);

    }

  }

  handleStatusChange({ values }) {

    const status = values[0];

    this.setAttendeeStatus(status);

  }

}

export default MeetingStatusControl;